/* ─── Warranty Support Helpers ─────────────────────────────────────────────── */

/**
 * Upload a PDF invoice and get warranty information from the backend.
 * Calls the Next.js proxy at /api/support/validate-invoice which forwards
 * the file to the backend POST /support/validate-invoice.
 * @param {File} file  A PDF File object
 */
export const checkWarrantyByInvoice = async (file) => {
    try {
        const form = new FormData();
        form.append('file', file);

        const res = await fetch('/api/support/validate-invoice', {
            method: 'POST',
            body: form,
        });

        return await res.json();
    } catch {
        return { success: false, message: 'Request failed. Please try again.' };
    }
};

/* ─── Per-card color palette (cycles by index) ──────────────────────────── */
export const CARD_PALETTES = [
    {
        color:    '#6366f1',
        dark:     '#4338ca',
        light:    '#eef2ff',
        muted:    'rgba(99,102,241,0.08)',
        border:   'rgba(99,102,241,0.25)',
        gradient: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        name:     'indigo',
    },
    {
        color:    '#1bb8a7',
        dark:     '#0f766e',
        light:    '#f0fdfa',
        muted:    'rgba(27,184,167,0.08)',
        border:   'rgba(27,184,167,0.25)',
        gradient: 'linear-gradient(135deg, #1bb8a7 0%, #0ea5e9 100%)',
        name:     'teal',
    },
    {
        color:    '#f97316',
        dark:     '#ea580c',
        light:    '#fff7ed',
        muted:    'rgba(249,115,22,0.08)',
        border:   'rgba(249,115,22,0.25)',
        gradient: 'linear-gradient(135deg, #f97316 0%, #ef4444 100%)',
        name:     'orange',
    },
    {
        color:    '#8b5cf6',
        dark:     '#7c3aed',
        light:    '#f5f3ff',
        muted:    'rgba(139,92,246,0.08)',
        border:   'rgba(139,92,246,0.25)',
        gradient: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
        name:     'violet',
    },
    {
        color:    '#0ea5e9',
        dark:     '#0284c7',
        light:    '#f0f9ff',
        muted:    'rgba(14,165,233,0.08)',
        border:   'rgba(14,165,233,0.25)',
        gradient: 'linear-gradient(135deg, #0ea5e9 0%, #6366f1 100%)',
        name:     'sky',
    },
    {
        color:    '#f43f5e',
        dark:     '#e11d48',
        light:    '#fff1f2',
        muted:    'rgba(244,63,94,0.08)',
        border:   'rgba(244,63,94,0.25)',
        gradient: 'linear-gradient(135deg, #f43f5e 0%, #f97316 100%)',
        name:     'rose',
    },
    {
        color:    '#10b981',
        dark:     '#059669',
        light:    '#f0fdf4',
        muted:    'rgba(16,185,129,0.08)',
        border:   'rgba(16,185,129,0.25)',
        gradient: 'linear-gradient(135deg, #10b981 0%, #1bb8a7 100%)',
        name:     'emerald',
    },
    {
        color:    '#06b6d4',
        dark:     '#1bb8a7',
        light:    '#ecfeff',
        muted:    'rgba(6,182,212,0.08)',
        border:   'rgba(6,182,212,0.25)',
        gradient: 'linear-gradient(135deg, #06b6d4 0%, #6366f1 100%)',
        name:     'cyan',
    },
];

/** Get card palette by index (cycles if more items than palettes) */
export const getCardPalette = (index) => CARD_PALETTES[index % CARD_PALETTES.length];

/* ─── Warranty status display config ────────────────────────────────────── */
export const getWarrantyStatusConfig = (status) => {
    if (status === 'in_warranty') {
        return {
            label:        'In Warranty',
            color:        '#16a34a',
            bg:           '#dcfce7',
            border:       'rgba(22,163,74,0.3)',
            icon:         'pi pi-shield',
            bannerBg:     'linear-gradient(135deg, rgba(16,185,129,0.12), rgba(5,150,105,0.08))',
            bannerBorder: 'rgba(16,185,129,0.3)',
            bannerIcon:   '#10b981',
        };
    }
    return {
        label:        'Out of Warranty',
        color:        '#dc2626',
        bg:           '#fee2e2',
        border:       'rgba(220,38,38,0.3)',
        icon:         'pi pi-times-circle',
        bannerBg:     'linear-gradient(135deg, rgba(239,68,68,0.1), rgba(249,115,22,0.08))',
        bannerBorder: 'rgba(239,68,68,0.3)',
        bannerIcon:   '#ef4444',
    };
};

/**
 * Formats a dd-mm-yyyy (or dd.mm.yyyy) date string → "04 Dec 2025"
 */
export const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const parts = dateStr.trim().split(/\s+/)[0].split(/[-./]/);
    if (parts.length !== 3) return dateStr;
    const [d, m, y] = parts;
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${String(d).padStart(2,'0')} ${months[Number(m)-1] || m} ${y}`;
};
